import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { cleanupLinks } from '../../../Components/TextEditor/Utils';
import {
  getSDEEvent,
  getEventRegistrationsWithCandidatures,
  getEventStats,
  updateRegistrationStatus,
  updateRegistrationAdminFields,
} from '../../Components/SDE24/sdeSupabaseService';
import { STATUS_COLORS, ADHERENT_OPTIONS, PLACE_DU_DESIGN_OPTIONS, PROFIL_CREATEUR_OPTIONS } from '../../Components/SDE24/sdeConstants';
import { sendSDOConfirmationEmail } from '../../../api';

// ─── Theme ─────────────────────────────────────────
const theme = {
  primary: '#2563EB',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  gray: { 50: '#F9FAFB', 100: '#F3F4F6', 200: '#E5E7EB', 300: '#D1D5DB', 400: '#9CA3AF', 500: '#6B7280', 600: '#4B5563', 700: '#374151', 800: '#1F2937' },
};

// ─── Styled ────────────────────────────────────────
const Page = styled.div` max-width: 1400px; margin: 0 auto; padding: 2rem; `;

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 2rem; flex-wrap: wrap; gap: 1rem;
  h1 { font-size: 1.75rem; font-weight: 700; color: ${theme.gray[800]}; margin: 0; }
`;

const BackBtn = styled.button`
  background: white; border: 1px solid ${theme.gray[300]}; padding: 0.5rem 1rem; border-radius: 8px; cursor: pointer; font-size: 0.875rem; color: ${theme.gray[700]};
  &:hover { background: ${theme.gray[50]}; }
`;

const StatsGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white; border: 1px solid ${theme.gray[200]}; border-radius: 12px; padding: 1.25rem;
  border-left: 4px solid ${p => p.accent || theme.primary};
  h3 { font-size: 0.8rem; font-weight: 600; color: ${theme.gray[500]}; text-transform: uppercase; margin: 0 0 0.5rem; }
  .value { font-size: 2rem; font-weight: 700; color: ${theme.gray[800]}; }
  .sub { font-size: 0.8rem; color: ${theme.gray[500]}; margin-top: 0.25rem; }
`;

const Tabs = styled.div`
  display: flex; gap: 0.5rem; margin-bottom: 1.5rem; border-bottom: 2px solid ${theme.gray[200]}; padding-bottom: 0;
`;

const Tab = styled.button`
  padding: 0.6rem 1.25rem; border: none; background: none; font-size: 0.95rem; font-weight: 600; cursor: pointer;
  color: ${p => p.active ? theme.primary : theme.gray[500]};
  border-bottom: 3px solid ${p => p.active ? theme.primary : 'transparent'};
  margin-bottom: -2px; transition: all 0.2s;
  &:hover { color: ${theme.primary}; }
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; background: white; border-radius: 12px; overflow: visible;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  th { background: ${theme.gray[50]}; padding: 0.75rem 1rem; text-align: left; font-size: 0.8rem; font-weight: 600; color: ${theme.gray[600]}; text-transform: uppercase; border-bottom: 1px solid ${theme.gray[200]}; }
  th.sortable { cursor: pointer; user-select: none; &:hover { color: ${theme.primary}; } }
  td { padding: 0.75rem 1rem; border-bottom: 1px solid ${theme.gray[100]}; font-size: 0.9rem; color: ${theme.gray[700]}; }
  tr:hover td { background: ${theme.gray[50]}; }
`;

const Badge = styled.span`
  display: inline-block; padding: 0.2rem 0.6rem; border-radius: 999px; font-size: 0.75rem; font-weight: 600;
  background: ${p => p.bg || theme.gray[100]}; color: ${p => p.fg || theme.gray[700]};
`;

const ActionBtn = styled.button`
  padding: 0.3rem 0.7rem; border-radius: 6px; font-size: 0.8rem; font-weight: 500; cursor: pointer; border: 1px solid ${theme.gray[300]}; background: white; color: ${theme.gray[700]}; transition: all 0.15s;
  &:hover { background: ${theme.gray[50]}; border-color: ${theme.gray[400]}; color: ${theme.gray[800]}; }
  & + & { margin-left: 0.35rem; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const SearchInput = styled.input`
  padding: 0.6rem 1rem; border: 1px solid ${theme.gray[300]}; border-radius: 8px; font-size: 0.9rem; width: 280px;
  &:focus { outline: none; border-color: ${theme.primary}; box-shadow: 0 0 0 3px ${theme.primary}20; }
`;

const ToolBar = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 1rem; flex-wrap: wrap; gap: 0.75rem;
`;

const ExportBtn = styled.button`
  padding: 0.5rem 1rem; border-radius: 8px; font-size: 0.85rem; font-weight: 600; cursor: pointer; border: 1px solid ${theme.primary}; color: ${theme.primary}; background: white;
  &:hover { background: ${theme.primary}10; }
`;

const LoadingBox = styled.div`
  display: flex; align-items: center; justify-content: center; min-height: 60vh; font-size: 1.1rem; color: ${theme.gray[500]};
`;

const EmailDropdown = styled.div`
  position: relative; display: inline-block;
`;

const EmailMenu = styled.div`
  position: absolute; right: 0; top: 100%; z-index: 10; background: white; border: 1px solid ${theme.gray[200]};
  border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); min-width: 240px; padding: 0.5rem 0;
`;

const EmailMenuItem = styled.button`
  display: block; width: 100%; text-align: left; padding: 0.5rem 1rem; border: none; background: none;
  font-size: 0.85rem; color: ${theme.gray[700]}; cursor: pointer;
  &:hover { background: ${theme.gray[50]}; }
  .desc { font-size: 0.75rem; color: ${theme.gray[400]}; }
`;

const AdherentBadge = styled.span`
  display: inline-block; padding: 0.15rem 0.5rem; border-radius: 4px; font-size: 0.7rem; font-weight: 500;
  background: #DBEAFE; color: #1E40AF; margin-right: 0.25rem;
`;

const EmailSentBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px; padding: 0.15rem 0.5rem; border-radius: 4px;
  font-size: 0.7rem; font-weight: 500; background: #D1FAE5; color: #065F46;
`;

const DetailRow = styled.tr`
  td { padding: 0 !important; border-bottom: 2px solid ${theme.primary}20 !important; }
`;

const DetailPanel = styled.div`
  padding: 1.25rem 1.5rem; background: ${theme.gray[50]};
  display: grid; grid-template-columns: 1fr 1fr; gap: 1rem 2rem;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const DetailSection = styled.div`
  background: white; border: 1px solid ${theme.gray[200]}; border-radius: 8px; padding: 0.75rem 1rem;
  h5 { font-size: 0.8rem; font-weight: 700; color: ${theme.primary}; text-transform: uppercase; margin: 0 0 0.5rem; letter-spacing: 0.5px; }
`;

const DetailField = styled.div`
  display: flex; gap: 0.5rem; padding: 0.2rem 0; font-size: 0.85rem;
  .label { color: ${theme.gray[500]}; min-width: 120px; flex-shrink: 0; }
  .value { color: ${theme.gray[800]}; font-weight: 500; word-break: break-word; }
`;

const DetailText = styled.div`
  font-size: 0.85rem; color: ${theme.gray[700]}; white-space: pre-wrap; padding: 0.25rem 0;
  background: ${theme.gray[50]}; border-radius: 4px; padding: 0.5rem; margin-top: 0.25rem;
  a { color: ${theme.primary}; text-decoration: underline; }
`;

const DetailTag = styled.span`
  display: inline-block; padding: 0.15rem 0.5rem; background: #DBEAFE; color: #1E40AF;
  border-radius: 4px; font-size: 0.75rem; font-weight: 500; margin: 0.15rem 0.15rem 0 0;
`;

const ProjectCard = styled.div`
  background: ${theme.gray[50]}; border: 1px solid ${theme.gray[200]}; border-radius: 6px; padding: 0.6rem 0.75rem; margin-bottom: 0.5rem;
  &:last-child { margin-bottom: 0; }
  .proj-num { font-size: 0.75rem; font-weight: 700; color: ${theme.primary}; margin-bottom: 0.25rem; }
`;

const ViewBtn = styled.button`
  padding: 0.3rem 0.7rem; border-radius: 6px; font-size: 0.8rem; font-weight: 500; cursor: pointer;
  border: 1px solid ${theme.primary}; background: ${theme.primary}10; color: ${theme.primary}; transition: all 0.15s;
  &:hover { background: ${theme.primary}20; }
`;

const EditInput = styled.input`
  padding: 0.25rem 0.4rem; border: 1px solid ${theme.primary}; border-radius: 4px; font-size: 0.85rem;
  width: 100%; max-width: 280px; color: ${theme.gray[800]};
  &:focus { outline: none; box-shadow: 0 0 0 2px ${theme.primary}30; }
`;

const EditTextarea = styled.textarea`
  padding: 0.35rem 0.5rem; border: 1px solid ${theme.primary}; border-radius: 4px; font-size: 0.85rem;
  width: 100%; min-height: 60px; resize: vertical; color: ${theme.gray[800]}; font-family: inherit;
  &:focus { outline: none; box-shadow: 0 0 0 2px ${theme.primary}30; }
`;

const AdminCommentsEditor = styled.div`
  .ql-toolbar { border: 1px solid ${theme.primary}; border-radius: 4px 4px 0 0; padding: 4px; }
  .ql-container { border: 1px solid ${theme.primary}; border-top: none; border-radius: 0 0 4px 4px; font-size: 0.85rem; min-height: 60px; }
  .ql-editor { min-height: 60px; padding: 0.35rem 0.5rem; }
  .ql-editor a { color: ${theme.primary}; text-decoration: underline; }
`;

const EditActions = styled.div`
  display: flex; gap: 4px; margin-top: 4px;
`;

const EditOk = styled.button`
  padding: 2px 10px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; cursor: pointer;
  border: none; background: ${theme.success}; color: white;
  &:hover { opacity: 0.85; }
`;

const EditCancel = styled.button`
  padding: 2px 10px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; cursor: pointer;
  border: 1px solid ${theme.gray[300]}; background: white; color: ${theme.gray[600]};
  &:hover { background: ${theme.gray[50]}; }
`;

const EditableValue = styled.span`
  color: ${p => p.hasValue ? theme.gray[800] : theme.gray[400]}; font-weight: 500; word-break: break-word;
  cursor: pointer; border-bottom: 1px dashed ${theme.gray[300]}; transition: all 0.15s;
  &:hover { border-bottom-color: ${theme.primary}; color: ${theme.primary}; }
`;

const CheckboxGroup = styled.div`
  display: flex; flex-direction: column; gap: 4px; padding: 0.25rem 0;
  label { display: flex; align-items: center; gap: 6px; font-size: 0.8rem; color: ${theme.gray[700]}; cursor: pointer;
    input { cursor: pointer; } }
`;

const SaveNotice = styled.span`
  font-size: 0.7rem; color: ${theme.success}; font-weight: 600; margin-left: 8px; animation: fadeIn 0.3s;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

// ─── Component ─────────────────────────────────────
const SDEAdmin = ({ fullUser }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventId = searchParams.get('eid');

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('pop_a');
  const [search, setSearch] = useState('');
  const [emailMenuOpen, setEmailMenuOpen] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [sortOrder, setSortOrder] = useState(null); // 'asc' | 'desc' | null
  const [editing, setEditing] = useState(null); // { regId, path }
  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState(null); // { regId, path } for flash feedback
  const commentsModules = useMemo(() => ({ toolbar: [['link']] }), []);

  useEffect(() => {
    if (!eventId) return;
    loadData();
    // eslint-disable-next-line
  }, [eventId]);

  // Close email menu on outside click
  useEffect(() => {
    const handler = () => setEmailMenuOpen(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [evRes, regsRes, statsRes] = await Promise.all([
      getSDEEvent(eventId),
      getEventRegistrationsWithCandidatures(eventId),
      getEventStats(eventId),
    ]);
    setEvent(evRes.data);
    setRegistrations(regsRes.data || []);
    setStats(statsRes);
    setLoading(false);
  };

  const filtered = registrations
    .filter(r => r.population_type === activeTab)
    .filter(r => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (r.company_name || '').toLowerCase().includes(s)
        || (r.user_email || '').toLowerCase().includes(s)
        || (r.representative_1?.name || '').toLowerCase().includes(s);
    })
    .sort((a, b) => {
      if (!sortOrder) return 0;
      const da = new Date(a.created_at).getTime();
      const db = new Date(b.created_at).getTime();
      return sortOrder === 'asc' ? da - db : db - da;
    });

  const toggleSort = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc');
  };

  const handleStatusChange = async (regId, newStatus) => {
    await updateRegistrationStatus(regId, newStatus);
    loadData();
  };

  const handleSendEmail = async (reg, variant) => {
    const contact = reg.representative_1 || {};
    const recipientName = [contact.firstname, contact.name].filter(Boolean).join(' ');

    setSendingEmail(reg.id);
    setEmailMenuOpen(null);

    try {
      await sendSDOConfirmationEmail({
        registrationId: reg.id,
        emailVariant: variant,
        recipientEmail: reg.user_email,
        recipientName,
        companyName: reg.company_name,
      });
      alert(`Email envoyé à ${reg.user_email}`);
      loadData();
    } catch (err) {
      alert(`Erreur: ${err.response?.data?.message || err.message}`);
    } finally {
      setSendingEmail(null);
    }
  };

  const getAdherentLabels = (metadata) => {
    const list = metadata?.adherent_cotisant || [];
    if (!list.length) return null;
    return list.map(v => {
      const opt = ADHERENT_OPTIONS.find(o => o.value === v);
      return opt ? opt.label : v;
    });
  };

  const resolveProfilLabels = (values) => {
    if (!values?.length) return [];
    return values.map(v => {
      const opt = PROFIL_CREATEUR_OPTIONS.find(o => o.value === v);
      return opt ? opt.label_fr : v;
    });
  };

  // ─── Inline editing helpers ──────────────────────────
  const isEditing = (regId, path) => editing?.regId === regId && editing?.path === path;
  const isSaved = (regId, path) => saved?.regId === regId && saved?.path === path;

  const startEdit = (regId, path, currentValue) => {
    setEditing({ regId, path });
    setDraft(currentValue ?? '');
  };

  const cancelEdit = () => { setEditing(null); setDraft(''); };

  const saveField = async (regId, path, value) => {
    const reg = registrations.find(r => r.id === regId);
    if (!reg) return;

    let payload;
    const projectMatch = path.match(/^projects\[(\d+)]\.(.+)$/);
    if (projectMatch) {
      const idx = parseInt(projectMatch[1], 10);
      const field = projectMatch[2];
      const updatedProjects = (reg.projects || []).map((p, i) => i === idx ? { ...p, [field]: value } : p);
      payload = { projects: updatedProjects };
    } else if (path.startsWith('representative_1.')) {
      const field = path.split('.')[1];
      payload = { representative_1: { ...reg.representative_1, [field]: value } };
    } else if (path.startsWith('representative_2.')) {
      const field = path.split('.')[1];
      payload = { representative_2: { ...(reg.representative_2 || {}), [field]: value } };
    } else if (path.startsWith('metadata.')) {
      const field = path.split('.')[1];
      payload = { metadata: { ...(reg.metadata || {}), [field]: value } };
    } else {
      payload = { [path]: value };
    }

    const { error } = await updateRegistrationAdminFields(regId, payload);
    if (error) { alert(`Erreur: ${error.message}`); return; }

    // Optimistic update
    setRegistrations(prev => prev.map(r => {
      if (r.id !== regId) return r;
      if (projectMatch) {
        const idx = parseInt(projectMatch[1], 10);
        const field = projectMatch[2];
        return { ...r, projects: (r.projects || []).map((p, i) => i === idx ? { ...p, [field]: value } : p) };
      }
      if (path.startsWith('representative_1.')) {
        return { ...r, representative_1: { ...r.representative_1, [path.split('.')[1]]: value } };
      }
      if (path.startsWith('representative_2.')) {
        return { ...r, representative_2: { ...(r.representative_2 || {}), [path.split('.')[1]]: value } };
      }
      if (path.startsWith('metadata.')) {
        return { ...r, metadata: { ...(r.metadata || {}), [path.split('.')[1]]: value } };
      }
      return { ...r, [path]: value };
    }));

    setEditing(null);
    setDraft('');
    setSaved({ regId, path });
    setTimeout(() => setSaved(null), 2000);
  };

  const editableText = (regId, path, currentValue, placeholder = 'Ajouter...') => {
    if (isEditing(regId, path)) {
      return (
        <div>
          <EditInput
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveField(regId, path, draft); if (e.key === 'Escape') cancelEdit(); }}
            autoFocus
          />
          <EditActions>
            <EditOk onClick={() => saveField(regId, path, draft)}>OK</EditOk>
            <EditCancel onClick={cancelEdit}>Annuler</EditCancel>
          </EditActions>
        </div>
      );
    }
    return (
      <>
        <EditableValue hasValue={!!currentValue} onClick={() => startEdit(regId, path, currentValue || '')}>
          {currentValue || placeholder}
        </EditableValue>
        {isSaved(regId, path) && <SaveNotice>Sauvegardé</SaveNotice>}
      </>
    );
  };

  const editableTextarea = (regId, path, currentValue, placeholder = 'Ajouter...') => {
    if (isEditing(regId, path)) {
      return (
        <div>
          <EditTextarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') cancelEdit(); }}
            autoFocus
          />
          <EditActions>
            <EditOk onClick={() => saveField(regId, path, draft)}>OK</EditOk>
            <EditCancel onClick={cancelEdit}>Annuler</EditCancel>
          </EditActions>
        </div>
      );
    }
    return (
      <div
        style={{ cursor: 'pointer', borderBottom: '1px dashed #D1D5DB', transition: 'all 0.15s' }}
        onClick={() => startEdit(regId, path, currentValue || '')}
        title="Cliquer pour éditer"
      >
        <DetailText style={{ cursor: 'pointer' }}>
          {currentValue || <span style={{ color: theme.gray[400] }}>{placeholder}</span>}
        </DetailText>
        {isSaved(regId, path) && <SaveNotice>Sauvegardé</SaveNotice>}
      </div>
    );
  };

  const editableRichComments = (regId, path, currentValue, placeholder = 'Ajouter...') => {
    if (isEditing(regId, path)) {
      return (
        <div>
          <AdminCommentsEditor>
            <ReactQuill
              theme="snow"
              value={draft}
              onChange={val => setDraft(cleanupLinks(val))}
              modules={commentsModules}
            />
          </AdminCommentsEditor>
          <EditActions>
            <EditOk onClick={() => saveField(regId, path, draft)}>OK</EditOk>
            <EditCancel onClick={cancelEdit}>Annuler</EditCancel>
          </EditActions>
        </div>
      );
    }
    const hasContent = currentValue && currentValue.replace(/<[^>]*>/g, '').trim();
    return (
      <div
        style={{ cursor: 'pointer', borderBottom: '1px dashed #D1D5DB', transition: 'all 0.15s' }}
        onClick={() => startEdit(regId, path, currentValue || '')}
        title="Cliquer pour éditer"
      >
        {hasContent ? (
          <DetailText style={{ cursor: 'pointer' }}>
            <span dangerouslySetInnerHTML={{ __html: currentValue }} style={{ whiteSpace: 'normal' }} />
          </DetailText>
        ) : (
          <DetailText style={{ cursor: 'pointer' }}>
            <span style={{ color: theme.gray[400] }}>{placeholder}</span>
          </DetailText>
        )}
        {isSaved(regId, path) && <SaveNotice>Sauvegardé</SaveNotice>}
      </div>
    );
  };

  const editableMultiSelect = (regId, path, currentValues, options, labelKey = 'label') => {
    const values = currentValues || [];
    if (isEditing(regId, path)) {
      const selected = Array.isArray(draft) ? draft : [];
      const toggle = (val) => {
        setDraft(prev => {
          const arr = Array.isArray(prev) ? prev : [];
          return arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];
        });
      };
      return (
        <div>
          <CheckboxGroup>
            {options.map(opt => (
              <label key={opt.value}>
                <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} />
                {opt[labelKey] || opt.label_fr || opt.label}
              </label>
            ))}
          </CheckboxGroup>
          <EditActions>
            <EditOk onClick={() => saveField(regId, path, Array.isArray(draft) ? draft : [])}>OK</EditOk>
            <EditCancel onClick={cancelEdit}>Annuler</EditCancel>
          </EditActions>
        </div>
      );
    }
    const resolveLabel = (v) => {
      const opt = options.find(o => o.value === v);
      return opt ? (opt[labelKey] || opt.label_fr || opt.label) : v;
    };
    return (
      <div
        style={{ cursor: 'pointer' }}
        onClick={() => { setEditing({ regId, path }); setDraft([...values]); }}
        title="Cliquer pour éditer"
      >
        {values.length > 0
          ? values.map((v, i) => <DetailTag key={i}>{resolveLabel(v)}</DetailTag>)
          : <span style={{ color: theme.gray[400], fontSize: '0.8rem', borderBottom: '1px dashed #D1D5DB' }}>Ajouter...</span>}
        {isSaved(regId, path) && <SaveNotice>Sauvegardé</SaveNotice>}
      </div>
    );
  };

  const renderDetail = (reg) => {
    const meta = reg.metadata || {};
    const rep1 = reg.representative_1 || {};
    const rep2 = reg.representative_2 || {};
    const projects = reg.projects || [];
    const isPopA = reg.population_type === 'pop_a';

    return (
      <DetailPanel>
        {/* Admin act-as shortcuts */}
        <DetailSection style={{ gridColumn: '1 / -1' }}>
          <h5>Mode admin</h5>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <ActionBtn onClick={() => navigate(actAsUrl('/evenements/sdo/register', reg.user_email, isPopA ? 'a' : 'b'))}>
              Modifier l'inscription à sa place
            </ActionBtn>
            {!isPopA && (
              <ActionBtn onClick={() => navigate(actAsUrl('/evenements/sdo/candidatures', reg.user_email))} style={{ color: theme.success, borderColor: theme.success }}>
                Modifier ses candidatures
              </ActionBtn>
            )}
            {!isPopA && (
              <ActionBtn onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/evenements/sdo/accord-confidentialite?rid=${reg.id}`); alert('Lien copié'); }}>
                Copier le lien accord de confidentialité
              </ActionBtn>
            )}
          </div>
        </DetailSection>

        {/* Contact */}
        <DetailSection>
          <h5>Contact principal</h5>
          <DetailField><span className="label">Entreprise</span><span className="value">{editableText(reg.id, 'company_name', reg.company_name)}</span></DetailField>
          <DetailField><span className="label">Email compte</span><span className="value">{editableText(reg.id, 'user_email', reg.user_email)}</span></DetailField>
          <DetailField><span className="label">Prénom</span><span className="value">{editableText(reg.id, 'representative_1.firstname', rep1.firstname)}</span></DetailField>
          <DetailField><span className="label">Nom</span><span className="value">{editableText(reg.id, 'representative_1.name', rep1.name)}</span></DetailField>
          <DetailField><span className="label">Email</span><span className="value">{editableText(reg.id, 'representative_1.email', rep1.email)}</span></DetailField>
          <DetailField><span className="label">Mobile</span><span className="value">{editableText(reg.id, 'representative_1.phone', rep1.phone)}</span></DetailField>
          <DetailField><span className="label">Fixe</span><span className="value">{editableText(reg.id, 'representative_1.landline', rep1.landline)}</span></DetailField>
          <DetailField><span className="label">Poste</span><span className="value">{editableText(reg.id, 'representative_1.job_title', rep1.job_title)}</span></DetailField>
          <DetailField><span className="label">Site web</span><span className="value">{editableText(reg.id, 'metadata.website', meta.website)}</span></DetailField>
          {isPopA && <DetailField><span className="label">Table</span><span className="value">{editableText(reg.id, 'metadata.table', meta.table)}</span></DetailField>}
          {isPopA && <DetailField><span className="label">Étage</span><span className="value">{editableText(reg.id, 'metadata.etage', meta.etage)}</span></DetailField>}
        </DetailSection>

        {/* Second contact */}
        <DetailSection>
          <h5>Second contact</h5>
          <DetailField><span className="label">Prénom</span><span className="value">{editableText(reg.id, 'representative_2.firstname', rep2.firstname)}</span></DetailField>
          <DetailField><span className="label">Nom</span><span className="value">{editableText(reg.id, 'representative_2.name', rep2.name)}</span></DetailField>
          <DetailField><span className="label">Email</span><span className="value">{editableText(reg.id, 'representative_2.email', rep2.email)}</span></DetailField>
          <DetailField><span className="label">Mobile</span><span className="value">{editableText(reg.id, 'representative_2.phone', rep2.phone)}</span></DetailField>
          <DetailField><span className="label">Fixe</span><span className="value">{editableText(reg.id, 'representative_2.landline', rep2.landline)}</span></DetailField>
          <DetailField><span className="label">Poste</span><span className="value">{editableText(reg.id, 'representative_2.job_title', rep2.job_title)}</span></DetailField>
        </DetailSection>

        {/* Offre & besoins (Pop A) */}
        {isPopA && (
          <DetailSection>
            <h5>Offre & besoins</h5>
            <div style={{ fontSize: '0.75rem', color: theme.gray[500], fontWeight: 600 }}>Offre / Typologie</div>
            {editableRichComments(reg.id, 'metadata.offre', meta.offre)}
            <div style={{ fontSize: '0.75rem', color: theme.gray[500], fontWeight: 600, marginTop: '0.5rem' }}>Savoir-faire</div>
            {editableTextarea(reg.id, 'metadata.savoir_faire', meta.savoir_faire)}
            <div style={{ fontSize: '0.75rem', color: theme.gray[500], fontWeight: 600, marginTop: '0.5rem' }}>Place du design</div>
            {editableMultiSelect(reg.id, 'metadata.place_du_design', meta.place_du_design, PLACE_DU_DESIGN_OPTIONS, 'label_fr')}
            <div style={{ fontSize: '0.75rem', color: theme.gray[500], fontWeight: 600, marginTop: '0.5rem' }}>Adhérent</div>
            {editableMultiSelect(reg.id, 'metadata.adherent_cotisant', meta.adherent_cotisant, ADHERENT_OPTIONS, 'label')}
          </DetailSection>
        )}

        {/* Pop B expertise */}
        {!isPopA && (
          <DetailSection>
            <h5>Expertise</h5>
            <div style={{ fontSize: '0.75rem', color: theme.gray[500], fontWeight: 600 }}>Profil</div>
            {editableMultiSelect(reg.id, 'metadata.profil_createur', meta.profil_createur, PROFIL_CREATEUR_OPTIONS, 'label_fr')}
            <div style={{ fontSize: '0.75rem', color: theme.gray[500], fontWeight: 600, marginTop: '0.5rem' }}>Savoir-faire</div>
            {editableTextarea(reg.id, 'metadata.savoir_faire', meta.savoir_faire)}
            <div style={{ fontSize: '0.75rem', color: theme.gray[500], fontWeight: 600, marginTop: '0.5rem' }}>Matériaux & techniques</div>
            {editableTextarea(reg.id, 'metadata.materiaux_techniques', meta.materiaux_techniques)}
            <div style={{ fontSize: '0.75rem', color: theme.gray[500], fontWeight: 600, marginTop: '0.5rem' }}>Démarche créative</div>
            {editableTextarea(reg.id, 'metadata.demarche_creative', meta.demarche_creative)}
          </DetailSection>
        )}

        {/* Projects (Pop A) */}
        {isPopA && projects.length > 0 && (
          <DetailSection>
            <h5>Projets ({projects.length})</h5>
            {projects.map((p, i) => {
              const profilLabels = resolveProfilLabels(p.profil_createur);
              return (
                <ProjectCard key={p.id || i}>
                  <div className="proj-num">Projet {i + 1}</div>
                  <div style={{ fontSize: '0.75rem', color: theme.gray[500], fontWeight: 600 }}>Description</div>
                  {editableTextarea(reg.id, `projects[${i}].description`, p.description)}
                  <DetailField><span className="label">Typologie</span><span className="value">{editableText(reg.id, `projects[${i}].typologie`, p.typologie)}</span></DetailField>
                  <DetailField><span className="label">Savoir-faire</span><span className="value">{editableText(reg.id, `projects[${i}].savoir_faire`, p.savoir_faire)}</span></DetailField>
                  <div style={{ fontSize: '0.75rem', color: theme.gray[500], fontWeight: 600, marginTop: '0.25rem' }}>Profil créateur</div>
                  {editableMultiSelect(reg.id, `projects[${i}].profil_createur`, p.profil_createur, PROFIL_CREATEUR_OPTIONS, 'label_fr')}
                  <DetailField><span className="label">Précisions</span><span className="value">{editableText(reg.id, `projects[${i}].profil_precisions`, p.profil_precisions)}</span></DetailField>
                  {p.files?.length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#666' }}>Fichiers ({p.files.length})</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.25rem' }}>
                        {p.files.map((f, fi) => (
                          <a key={fi} href={f.publicUrl} target="_blank" rel="noreferrer" style={{ display: 'block', width: 60, height: 60, borderRadius: 4, overflow: 'hidden', border: '1px solid #ddd' }}>
                            {f.mimetype?.startsWith('image/') ? (
                              <img src={f.publicUrl} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', fontSize: '0.55rem', textAlign: 'center', color: '#888', padding: 4 }}>{f.name}</div>
                            )}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </ProjectCard>
              );
            })}
          </DetailSection>
        )}

        {/* Portfolio (Pop B) */}
        {!isPopA && reg.portfolio_projects?.length > 0 && (
          <DetailSection>
            <h5>Portfolio ({reg.portfolio_projects.length})</h5>
            {reg.portfolio_projects.map((p, i) => (
              <ProjectCard key={i}>
                <div className="proj-num">{p.title || `Projet ${i + 1}`}</div>
                {p.description && <DetailText>{p.description}</DetailText>}
                {p.confidentiality && <DetailField><span className="label">Confidentialité</span><span className="value">{p.confidentiality === 'confidentiel' ? 'Confidentiel' : 'Public'}</span></DetailField>}
                {p.files?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.25rem' }}>
                    {p.files.map((f, fi) => (
                      <a key={fi} href={f.publicUrl} target="_blank" rel="noreferrer" style={{ display: 'block', width: 60, height: 60, borderRadius: 4, overflow: 'hidden', border: '1px solid #ddd' }}>
                        {f.mimetype?.startsWith('image/') ? (
                          <img src={f.publicUrl} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', fontSize: '0.55rem', textAlign: 'center', color: '#888', padding: 4 }}>{f.name}</div>
                        )}
                      </a>
                    ))}
                  </div>
                )}
              </ProjectCard>
            ))}
          </DetailSection>
        )}

        {/* Comments */}
        <DetailSection>
          <h5>Commentaires</h5>
          {editableRichComments(reg.id, 'metadata.comments', meta.comments, 'Ajouter un commentaire...')}
        </DetailSection>
      </DetailPanel>
    );
  };

  const exportCSV = () => {
    const isPopB = activeTab === 'pop_b';
    const rows = filtered.map(r => {
      const adherentLabels = getAdherentLabels(r.metadata);
      const base = {
        company: r.company_name,
        prenom: r.representative_1?.firstname || '',
        nom: r.representative_1?.name || '',
        email: r.user_email,
        status: r.status,
        phone: r.representative_1?.phone || '',
        landline: r.representative_1?.landline || '',
        job_title: r.representative_1?.job_title || '',
        website: r.metadata?.website || '',
      };
      if (isPopB) {
        base.profil_createur = resolveProfilLabels(r.metadata?.profil_createur).join(', ');
        const cands = r.candidatures_as_designer || [];
        base.nombre_de_rdv = cands.length;
        base.rdv_confirmes = (r.meetings_as_designer || []).length;
        base.accord_signe = r.metadata?.accord_signe_url ? 'Oui' : 'Non';
        base.accord_signe_url = r.metadata?.accord_signe_url || '';
      } else {
        base.adherent = adherentLabels ? adherentLabels.join(', ') : '';
        base.table = r.metadata?.table || '';
        base.etage = r.metadata?.etage || '';
      }
      base.email_sent = r.metadata?.email_sent_date ? 'Oui' : 'Non';
      base.created = r.created_at;
      return base;
    });
    const headers = Object.keys(rows[0] || {});
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${(r[h] || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sdo_${activeTab}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const actAsUrl = (path, email, pop = 'b') => `${path}?eid=${eventId}&pop=${pop}&as=${encodeURIComponent(email)}`;

  const handleAddRegistration = (pop) => {
    const email = window.prompt(pop === 'a'
      ? "Email de l'entreprise à inscrire (Pop A) :"
      : "Email du designer à inscrire (Pop B) :");
    if (!email || !email.trim()) return;
    navigate(actAsUrl('/evenements/sdo/register', email.trim(), pop));
  };

  if (loading) return <LoadingBox>Chargement de l'administration SDO...</LoadingBox>;

  const statusInfo = (status) => STATUS_COLORS[status] || { bg: '#f0f0f0', text: '#666', label_fr: status };

  return (
    <Page>
      <Header>
        <div>
          <BackBtn onClick={() => navigate('/admin/events')}>← Retour aux événements</BackBtn>
          <h1 style={{ marginTop: '0.75rem' }}>{event?.title_fr || 'SDO Admin'}</h1>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <ActionBtn onClick={() => handleAddRegistration('a')} style={{ color: theme.primary, borderColor: theme.primary }}>
            + Inscrire un Pop A
          </ActionBtn>
          <ActionBtn onClick={() => handleAddRegistration('b')} style={{ color: theme.success, borderColor: theme.success }}>
            + Inscrire un Pop B
          </ActionBtn>
          <ActionBtn onClick={() => navigate(`/admin/events/sdo/shortlist?eid=${eventId}`)}>
            Shortlists
          </ActionBtn>
          <ActionBtn onClick={() => navigate(`/admin/events/sdo/planning?eid=${eventId}`)}>
            Planning
          </ActionBtn>
        </div>
      </Header>

      {/* Stats */}
      {stats && (
        <StatsGrid>
          <StatCard accent={theme.primary}>
            <h3>Pop A (Entreprises)</h3>
            <div className="value">{stats.popA.total}</div>
            <div className="sub">{stats.popA.validated} validées · {stats.popA.submitted} soumises</div>
          </StatCard>
          <StatCard accent={theme.success}>
            <h3>Pop B (Designers)</h3>
            <div className="value">{stats.popB.total}</div>
            <div className="sub">{stats.popB.validated} validées · {stats.popB.submitted} soumises</div>
          </StatCard>
          <StatCard accent={theme.warning}>
            <h3>Candidatures</h3>
            <div className="value">{stats.candidatures.total}</div>
            <div className="sub">{stats.candidatures.confirmed} confirmées · {stats.candidatures.pending} en attente</div>
          </StatCard>
          <StatCard accent="#8B5CF6">
            <h3>Rencontres</h3>
            <div className="value">{stats.meetings.total}</div>
            <div className="sub">planifiées</div>
          </StatCard>
        </StatsGrid>
      )}

      {/* Tabs */}
      <Tabs>
        <Tab active={activeTab === 'pop_a'} onClick={() => setActiveTab('pop_a')}>
          Pop A — Entreprises ({registrations.filter(r => r.population_type === 'pop_a').length})
        </Tab>
        <Tab active={activeTab === 'pop_b'} onClick={() => setActiveTab('pop_b')}>
          Pop B — Designers ({registrations.filter(r => r.population_type === 'pop_b').length})
        </Tab>
      </Tabs>

      <ToolBar>
        <SearchInput
          placeholder="Rechercher par nom, email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <ExportBtn onClick={exportCSV}>Exporter CSV ({filtered.length})</ExportBtn>
      </ToolBar>

      {/* Registration Table */}
      <Table>
        <thead>
          <tr>
            <th>Entreprise</th>
            <th>Contact</th>
            <th>Email</th>
            {activeTab === 'pop_a' && <th>Adhérent</th>}
            <th>Statut</th>
            {activeTab === 'pop_b' && <th>Candidatures</th>}
            {activeTab === 'pop_b' && <th>RDV confirmés</th>}
            {activeTab === 'pop_b' && <th>Accord signé</th>}
            <th>Email envoyé</th>
            <th className="sortable" onClick={toggleSort}>
              Date {sortOrder === 'asc' ? '↑' : sortOrder === 'desc' ? '↓' : '↕'}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(reg => {
            const si = statusInfo(reg.status);
            const contact = reg.representative_1;
            const candCount = activeTab === 'pop_b'
              ? (reg.candidatures_as_designer || []).length
              : (reg.candidatures_as_company || []).length;
            const confirmedCount = (reg.meetings_as_designer || []).length;
            const adherentLabels = getAdherentLabels(reg.metadata);
            const emailSent = reg.metadata?.email_sent_date;

            return (
              <React.Fragment key={reg.id}>
              <tr>
                <td style={{ fontWeight: 600 }}>{editableText(reg.id, 'company_name', reg.company_name, '—')}</td>
                <td>{contact ? `${contact.firstname || ''} ${contact.name || ''}` : '—'}</td>
                <td>{editableText(reg.id, 'user_email', reg.user_email, '—')}</td>
                {activeTab === 'pop_a' && (
                  <td>
                    {adherentLabels
                      ? adherentLabels.map((l, i) => <AdherentBadge key={i}>{l}</AdherentBadge>)
                      : <span style={{ color: theme.gray[400], fontSize: '0.8rem' }}>—</span>}
                  </td>
                )}
                <td>
                  <Badge bg={si.bg} fg={si.text}>{si.label_fr}</Badge>
                </td>
                {activeTab === 'pop_b' && <td>{candCount}</td>}
                {activeTab === 'pop_b' && <td>{confirmedCount}</td>}
                {activeTab === 'pop_b' && (
                  <td>
                    {reg.metadata?.accord_signe_url
                      ? <a href={reg.metadata.accord_signe_url} target="_blank" rel="noreferrer" title="Voir l'accord signé"><EmailSentBadge>&#10003; {reg.metadata.accord_signe_at ? new Date(reg.metadata.accord_signe_at).toLocaleDateString('fr-FR') : 'signé'}</EmailSentBadge></a>
                      : <span style={{ color: theme.gray[400], fontSize: '0.8rem' }}>—</span>}
                  </td>
                )}
                <td>
                  {emailSent
                    ? <EmailSentBadge>&#10003; {new Date(emailSent).toLocaleDateString('fr-FR')}</EmailSentBadge>
                    : <span style={{ color: theme.gray[400], fontSize: '0.8rem' }}>—</span>}
                </td>
                <td style={{ fontSize: '0.8rem', color: theme.gray[500] }}>
                  {new Date(reg.created_at).toLocaleDateString('fr-FR')}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <ViewBtn onClick={() => setExpandedRow(expandedRow === reg.id ? null : reg.id)}>
                    {expandedRow === reg.id ? 'Fermer' : 'Voir'}
                  </ViewBtn>
                  {reg.status === 'submitted' && (
                    <>
                      <ActionBtn onClick={() => handleStatusChange(reg.id, 'validated')} style={{ color: theme.success, borderColor: theme.success, marginLeft: '0.35rem' }}>
                        Valider
                      </ActionBtn>
                      <ActionBtn onClick={() => handleStatusChange(reg.id, 'rejected')} style={{ color: theme.error, borderColor: theme.error }}>
                        Refuser
                      </ActionBtn>
                    </>
                  )}
                  {reg.status === 'validated' && (
                    <ActionBtn onClick={() => handleStatusChange(reg.id, 'submitted')} style={{ color: theme.warning, borderColor: theme.warning, marginLeft: '0.35rem' }}>
                      Dévalider
                    </ActionBtn>
                  )}
                  {reg.status === 'rejected' && (
                    <ActionBtn onClick={() => handleStatusChange(reg.id, 'submitted')} style={{ color: theme.primary, borderColor: theme.primary, marginLeft: '0.35rem' }}>
                      Rétablir
                    </ActionBtn>
                  )}
                  <EmailDropdown onClick={e => e.stopPropagation()}>
                    <ActionBtn
                      onClick={() => setEmailMenuOpen(emailMenuOpen === reg.id ? null : reg.id)}
                      disabled={sendingEmail === reg.id}
                      style={{ marginLeft: '0.35rem' }}
                    >
                      {sendingEmail === reg.id ? '...' : '✉'}
                    </ActionBtn>
                    {emailMenuOpen === reg.id && (
                      <EmailMenu>
                        <EmailMenuItem onClick={() => handleSendEmail(reg, 'adherent')}>
                          Adhérent (pas de paiement)
                          <div className="desc">CODIFAB / AF / UNAMA</div>
                        </EmailMenuItem>
                        <EmailMenuItem onClick={() => handleSendEmail(reg, 'non_adherent')}>
                          Non-adhérent (adhésion + participation)
                          <div className="desc">35€ + 100€ via Stripe</div>
                        </EmailMenuItem>
                        <EmailMenuItem onClick={() => handleSendEmail(reg, 'membre_connected')}>
                          Membre Connected (participation seule)
                          <div className="desc">100€ via Stripe</div>
                        </EmailMenuItem>
                      </EmailMenu>
                    )}
                  </EmailDropdown>
                </td>
              </tr>
              {expandedRow === reg.id && (
                <DetailRow>
                  <td colSpan={activeTab === 'pop_b' ? 10 : 8}>
                    {renderDetail(reg)}
                  </td>
                </DetailRow>
              )}
              </React.Fragment>
            );
          })}
          {filtered.length === 0 && (
            <tr><td colSpan={activeTab === 'pop_b' ? 10 : 8} style={{ textAlign: 'center', padding: '2rem', color: theme.gray[400] }}>Aucune inscription trouvée</td></tr>
          )}
        </tbody>
      </Table>
    </Page>
  );
};

export default SDEAdmin;
